import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import i18n from '@emoji-mart/data/i18n/zh.json';
import { Popover } from 'antd';
import { ChatInputProps } from '../../../types/chat';
import VoiceRecorder from './VoiceRecorder';
import { INPUT_MODE_STORAGE_KEY, INPUT_MODE_DEFAULTS } from '../constants';

interface EmojiData {
    native: string;
}

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
    border-top: 1px solid #ccc;
    background-color: #f9f9f9;
`;

const InputRow = styled.div<{ isVoiceMode: boolean }>`
    display: flex;
    align-items: center;
    background-color: #fff;
    border-radius: 25px;
    justify-content: space-between;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    padding: 5px 10px;
    gap: 8px;
    min-height: 30px;
`;

const Input = styled.input`
    flex: 1;
    padding: 10px 5px;
    border: none;
    border-radius: 25px;
    outline: none;
    color: #333;
    background-color: transparent;
    font-size: 16px;
`;

const EmojiButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    margin: 0;
    padding: 5px;
    font-size: 16px;
    
    &:hover {
        color: #007bff;
    }
`;

const SendButton = styled.button`
    padding: 10px 15px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 25px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 14px;
    
    &:hover {
        background-color: #0056b3;
    }
`;

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled, threadId, accountId }) => {
    const [inputValue, setInputValue] = useState('');
    const [visible, setVisible] = useState(false);
    const [isVoiceMode, setIsVoiceMode] = useState(() => {
        try {
            const savedMode = localStorage.getItem(INPUT_MODE_STORAGE_KEY);
            // 如果localStorage中没有值，或者值无效，使用默认的文字输入模式
            return savedMode === null ? INPUT_MODE_DEFAULTS.DEFAULT_MODE : JSON.parse(savedMode);
        } catch (error) {
            // 如果解析出错，也使用默认的文字输入模式
            console.error('Error reading input mode from localStorage:', error);
            return INPUT_MODE_DEFAULTS.DEFAULT_MODE;
        }
    });

    useEffect(() => {
        localStorage.setItem(INPUT_MODE_STORAGE_KEY, JSON.stringify(isVoiceMode));
    }, [isVoiceMode]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleSend = (text: string) => {
        if (text.trim() && !disabled) {
            onSend(text);
            setInputValue('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !disabled) {
            handleSend(inputValue);
        }
    };

    const addEmoji = (emoji: EmojiData) => {
        setInputValue(inputValue + emoji.native);
    };

    const handleVoiceRecordingComplete = async (blob: Blob) => {
        if (disabled) return;

        // 创建一个 FormData 对象来发送音频文件
        const formData = new FormData();
        formData.append('audio', blob, 'voice-message.wav');

        try {
            // 这里需要实现上传音频文件的逻辑
            // const response = await uploadAudio(formData);
            // onSend(response.url); // 发送音频文件的 URL
            console.log('Voice recording completed:', blob);
        } catch (error) {
            console.error('Failed to upload voice recording:', error);
        }
    };

    return (
        <InputContainer>
            <InputRow isVoiceMode={isVoiceMode}>
                <VoiceRecorder
                    onRecordingComplete={handleVoiceRecordingComplete}
                    onModeChange={setIsVoiceMode}
                    isVoiceMode={isVoiceMode}
                    threadId={threadId}
                    accountId={accountId}
                    onSendMessage={(text) => handleSend(text)}
                />
                {!isVoiceMode && (
                    <>
                        <Input
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="输入消息..."
                            disabled={disabled}
                        />
                        <Popover
                            content={<Picker data={data} i18n={i18n} onEmojiSelect={addEmoji} />}
                            trigger="click"
                            open={visible}
                            onOpenChange={setVisible}
                        >
                            <EmojiButton>😊</EmojiButton>
                        </Popover>
                        <SendButton
                            onClick={() => handleSend(inputValue)}
                            disabled={disabled}
                        >
                            发送
                        </SendButton>
                    </>
                )}
            </InputRow>
        </InputContainer>
    );
};

export default ChatInput; 